import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

export default function AudioPlayer({ src, label, variant = "off", testId }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      setCurrent(a.currentTime);
      setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
    };
    const onMeta = () => setDuration(a.duration || 0);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      // Pause other players on the page
      document.querySelectorAll("audio").forEach((el) => {
        if (el !== a) el.pause();
      });
      a.play();
      setPlaying(true);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * a.duration;
  };

  const fmt = (s) => {
    if (!s || !isFinite(s)) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const isOn = variant === "on";

  return (
    <div
      className={`relative border rounded-xl p-5 transition-all ${
        isOn
          ? "border-cyan-200/40 bg-cyan-200/[0.03]"
          : "border-white/10 bg-black/40"
      }`}
      data-testid={testId}
    >
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="flex items-center justify-between mb-4">
        <span
          className={`text-[10px] uppercase tracking-[0.25em] font-mono-tech ${
            isOn ? "text-cyan-200" : "text-zinc-500"
          }`}
        >
          {label}
        </span>
        <span className="text-[10px] font-mono-tech text-zinc-500">
          {fmt(current)} / {fmt(duration)}
        </span>
      </div>
      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          className={`flex-shrink-0 h-11 w-11 rounded-full flex items-center justify-center transition-all ${
            isOn
              ? "bg-cyan-200 text-black hover:bg-white"
              : "bg-white text-black hover:bg-cyan-200"
          }`}
          aria-label={playing ? "Pause" : "Play"}
          data-testid={testId ? `${testId}-play` : undefined}
        >
          {playing ? (
            <Pause className="h-4 w-4" fill="currentColor" />
          ) : (
            <Play className="h-4 w-4 ml-0.5" fill="currentColor" />
          )}
        </button>
        <div
          onClick={seek}
          className="flex-1 h-1.5 bg-white/10 rounded-full cursor-pointer relative overflow-hidden group"
        >
          <div
            className={`absolute inset-y-0 left-0 rounded-full transition-[width] duration-75 ${
              isOn ? "bg-cyan-200" : "bg-white"
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}
